import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Chip,
  Stack,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Preview as PreviewIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import {
  ComponentDefinition,
  CreateComponentRequest,
  ComponentProp,
  COMPONENT_CATEGORIES,
  COMPONENT_PROP_TYPES
} from '../../types/components';

interface ComponentBuilderProps {
  component?: ComponentDefinition | null;
  onSave: (componentData: CreateComponentRequest) => Promise<void>;
  onCancel: () => void;
}

const ComponentBuilder: React.FC<ComponentBuilderProps> = ({ component, onSave, onCancel }) => {
  const [componentData, setComponentData] = useState<CreateComponentRequest>({
    name: '',
    displayName: '',
    description: '',
    category: 'Content',
    props: [],
    htmlTemplate: '<div>{{content}}</div>',
    cssTemplate: '',
    jsTemplate: '',
    dependencies: [],
    tags: []
  });

  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (component) {
      setComponentData({
        name: component.name,
        displayName: component.displayName,
        description: component.description || '',
        category: component.category,
        icon: component.icon,
        preview: component.preview,
        props: component.props,
        defaultStyling: component.defaultStyling,
        htmlTemplate: component.htmlTemplate,
        cssTemplate: component.cssTemplate || '',
        jsTemplate: component.jsTemplate || '',
        dependencies: component.dependencies || [],
        tags: component.tags || []
      });
    }
  }, [component]);

  const handleBasicInfoChange = (field: keyof CreateComponentRequest, value: unknown) => {
    setComponentData(prev => ({
      ...prev,
      [field]: value
    }));
    setValidationErrors([]);
  };

  const addProp = () => {
    const newProp: ComponentProp = {
      name: '',
      type: 'string',
      label: '',
      required: false
    };

    setComponentData(prev => ({
      ...prev,
      props: [...prev.props, newProp]
    }));
  };

  const updateProp = (index: number, updates: Partial<ComponentProp>) => {
    setComponentData(prev => ({
      ...prev,
      props: prev.props.map((prop, i) => 
        i === index ? { ...prop, ...updates } : prop
      )
    }));
  };

  const removeProp = (index: number) => {
    setComponentData(prev => ({
      ...prev,
      props: prev.props.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!componentData.name?.trim()) {
      errors.push('Component name is required');
    }

    if (!componentData.displayName?.trim()) {
      errors.push('Display name is required');
    }

    if (!componentData.htmlTemplate?.trim()) {
      errors.push('HTML template is required');
    }

    // Validate props
    componentData.props.forEach((prop, index) => {
      if (!prop.name?.trim()) {
        errors.push(`Property ${index + 1}: Name is required`);
      }
      if (!prop.label?.trim()) {
        errors.push(`Property ${index + 1}: Label is required`);
      }
    });

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setSaving(true);
      await onSave(componentData);
    } catch (error) {
      console.error('Error saving component:', error);
    } finally {
      setSaving(false);
    }
  };

  const generatePreviewHTML = () => {
    let html = componentData.htmlTemplate;
    
    // Replace placeholders with sample data
    componentData.props.forEach(prop => {
      const placeholder = `{{${prop.name}}}`;
      let sampleValue = '';
      
      switch (prop.type) {
        case 'string':
          sampleValue = prop.defaultValue as string || `Sample ${prop.label}`;
          break;
        case 'number':
          sampleValue = String(prop.defaultValue || 42);
          break;
        case 'boolean':
          sampleValue = String(prop.defaultValue || true);
          break;
        default:
          sampleValue = String(prop.defaultValue || `Sample ${prop.label}`);
      }
      
      html = html.replace(new RegExp(placeholder, 'g'), sampleValue);
    });

    return html;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onCancel} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
          {component ? 'Edit Component' : 'Create Component'}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={() => setShowPreview(!showPreview)}
          sx={{ mr: 2 }}
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !componentData.name || !componentData.displayName}
          sx={{ borderRadius: 2 }}
        >
          {saving ? 'Saving...' : 'Save Component'}
        </Button>
      </Box>

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">Please fix the following errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Configuration */}
        <Grid size={{ xs: 12, md: showPreview ? 6 : 8 }}>
          {/* Basic Information */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Basic Information
            </Typography>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Component Name"
                  value={componentData.name}
                  onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                  required
                  helperText="Internal name (lowercase, no spaces)"
                />
                <TextField
                  fullWidth
                  label="Display Name"
                  value={componentData.displayName}
                  onChange={(e) => handleBasicInfoChange('displayName', e.target.value)}
                  required
                  helperText="User-friendly name"
                />
              </Box>

              <TextField
                fullWidth
                label="Description"
                value={componentData.description}
                onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                multiline
                rows={2}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={componentData.category}
                    label="Category"
                    onChange={(e) => handleBasicInfoChange('category', e.target.value)}
                  >
                    {COMPONENT_CATEGORIES.map(category => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Icon"
                  value={componentData.icon || ''}
                  onChange={(e) => handleBasicInfoChange('icon', e.target.value)}
                  helperText="Material UI icon name"
                />
              </Box>

              <TextField
                fullWidth
                label="Tags"
                value={componentData.tags?.join(', ') || ''}
                onChange={(e) => handleBasicInfoChange('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                helperText="Comma-separated tags"
              />
            </Stack>
          </Paper>

          {/* Properties */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Component Properties ({componentData.props.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addProp}
                size="small"
              >
                Add Property
              </Button>
            </Box>

            {componentData.props.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                color: 'text.secondary',
                border: '2px dashed #e0e0e0',
                borderRadius: 2
              }}>
                <Typography variant="body1">
                  No properties defined. Click "Add Property" to get started.
                </Typography>
              </Box>
            ) : (
              componentData.props.map((prop, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Property {index + 1}: {prop.label || 'Untitled'}
                      </Typography>
                      <IconButton size="small" color="error" onClick={() => removeProp(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Property Name"
                          value={prop.name}
                          onChange={(e) => updateProp(index, { name: e.target.value })}
                          size="small"
                          helperText="Used in template (no spaces)"
                        />
                        <TextField
                          fullWidth
                          label="Label"
                          value={prop.label}
                          onChange={(e) => updateProp(index, { label: e.target.value })}
                          size="small"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={prop.type}
                            label="Type"
                            onChange={(e) => updateProp(index, { type: e.target.value as ComponentProp['type'] })}
                          >
                            {COMPONENT_PROP_TYPES.map(type => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          label="Default Value"
                          value={prop.defaultValue as string || ''}
                          onChange={(e) => updateProp(index, { defaultValue: e.target.value })}
                          size="small"
                        />
                      </Box>

                      <TextField
                        fullWidth
                        label="Description"
                        value={prop.description || ''}
                        onChange={(e) => updateProp(index, { description: e.target.value })}
                        size="small"
                        multiline
                        rows={1}
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={prop.required || false}
                            onChange={(e) => updateProp(index, { required: e.target.checked })}
                          />
                        }
                        label="Required"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))
            )}
          </Paper>

          {/* Templates */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon />
                <Typography variant="h6">Templates</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="HTML Template"
                  value={componentData.htmlTemplate}
                  onChange={(e) => handleBasicInfoChange('htmlTemplate', e.target.value)}
                  multiline
                  rows={8}
                  required
                  helperText="Use {{propertyName}} for property placeholders"
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />

                <TextField
                  fullWidth
                  label="CSS Template (Optional)"
                  value={componentData.cssTemplate}
                  onChange={(e) => handleBasicInfoChange('cssTemplate', e.target.value)}
                  multiline
                  rows={4}
                  helperText="CSS styles for the component"
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />

                <TextField
                  fullWidth
                  label="JavaScript Template (Optional)"
                  value={componentData.jsTemplate}
                  onChange={(e) => handleBasicInfoChange('jsTemplate', e.target.value)}
                  multiline
                  rows={4}
                  helperText="JavaScript for interactive components"
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Preview Panel */}
        {showPreview && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2.5, position: 'sticky', top: 20 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Preview
              </Typography>

              <Box 
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  minHeight: 200,
                  backgroundColor: '#fafafa'
                }}
                dangerouslySetInnerHTML={{ __html: generatePreviewHTML() }}
              />

              {componentData.cssTemplate && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>CSS:</Typography>
                  <Box 
                    component="pre"
                    sx={{ 
                      backgroundColor: '#f5f5f5',
                      p: 1,
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      overflow: 'auto',
                      maxHeight: 200
                    }}
                  >
                    {componentData.cssTemplate}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Sidebar Configuration */}
        {!showPreview && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2.5, position: 'sticky', top: 20 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Configuration
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Dependencies</Typography>
                  <TextField
                    fullWidth
                    label="External Dependencies"
                    value={componentData.dependencies?.join(', ') || ''}
                    onChange={(e) => handleBasicInfoChange('dependencies', e.target.value.split(',').map(d => d.trim()).filter(Boolean))}
                    helperText="Comma-separated list"
                    size="small"
                    multiline
                    rows={2}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Template Help</Typography>
                  <Alert severity="info">
                    Use <code>{'{{propertyName}}'}</code> in your HTML template to insert property values.
                  </Alert>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Available Properties</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {componentData.props.map((prop, index) => (
                      <Chip
                        key={index}
                        label={`{{${prop.name}}}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ComponentBuilder;